<template>
  <div class="user-management-page">
    <!-- Header Section -->
    <header class="dashboard-header">
      <div class="header-content">
        <h1>User Management</h1>
        <p class="subtitle">Manage users and admins for your subsidiary</p>
      </div>
    </header>

    <!-- Main Content -->
    <div class="management-container">
      <!-- Search and Filter Section -->
      <div class="filters-section">
        <div class="search-box">
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Search users..."
            @input="handleSearch"
          />
          <i class="fa-solid fa-search"></i>
        </div>
        <div class="filter-group">
          <select v-model="roleFilter" @change="handleFilter">
            <option value="">All Roles</option>
            <option value="Admin">Admin</option>
            <option value="Person-In-Charge">Person-In-Charge</option>
          </select>
        </div>
      </div>

      <!-- Users Table -->
      <div class="card">
        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Branch</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in filteredUsers" :key="user.id">
                <td>{{ user.name }}</td>
                <td>{{ user.email }}</td>
                <td>{{ user.role }}</td>
                <td>{{ user.branch_name || 'N/A' }}</td>
                <td>
                  <span :class="['status-badge', user.status.toLowerCase()]">
                    {{ user.status }}
                  </span>
                </td>
                <td class="actions">
                  <button 
                    class="action-btn edit"
                    @click="editUser(user)"
                    title="Edit User"
                  >
                    <i class="fa-solid fa-edit"></i>
                  </button>
                  <button 
                    class="action-btn delete"
                    @click="confirmDelete(user)"
                    title="Delete User"
                  >
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Edit User Modal -->
    <div class="modal" v-if="showEditModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Edit User</h2>
          <button class="close-btn" @click="closeEditModal">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleEditSubmit">
            <div class="form-group">
              <label for="editName">Full Name</label>
              <input 
                type="text" 
                id="editName" 
                v-model="editForm.name"
                required
              />
            </div>
            <div class="form-group">
              <label for="editEmail">Email</label>
              <input 
                type="email" 
                id="editEmail" 
                v-model="editForm.email"
                required
              />
            </div>
            <div class="form-group">
              <label for="editRole">Role</label>
              <select id="editRole" v-model="editForm.role" required>
                <option value="Admin">Admin</option>
                <option value="Person-In-Charge">Person-In-Charge</option>
              </select>
            </div>
            <div class="form-group" v-if="editForm.role === 'Person-In-Charge'">
              <label for="editBranch">Branch</label>
              <select id="editBranch" v-model="editForm.branch_id">
                <option value="">Select Branch</option>
                <option v-for="branch in branches" :key="branch.id" :value="branch.id">
                  {{ branch.branch_name }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="editStatus">Status</label>
              <select id="editStatus" v-model="editForm.status" required>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div class="form-actions">
              <button type="button" class="btn-secondary" @click="closeEditModal">Cancel</button>
              <button type="submit" class="btn-primary" :disabled="isLoading">
                {{ isLoading ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal" v-if="showDeleteModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Confirm Delete</h2>
          <button class="close-btn" @click="closeDeleteModal">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete this user? This action cannot be undone.</p>
          <div class="form-actions">
            <button type="button" class="btn-secondary" @click="closeDeleteModal">Cancel</button>
            <button type="button" class="btn-danger" @click="handleDelete" :disabled="isLoading">
              {{ isLoading ? 'Deleting...' : 'Delete User' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <div class="toast-container" v-if="showToast">
      <div :class="['toast', toastType]">
        <span class="icon" v-if="toastType === 'success'">✓</span>
        {{ toastMessage }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const isLoading = ref(false)
const showToast = ref(false)
const toastMessage = ref('')
const toastType = ref('')
const users = ref([])
const branches = ref([])
const searchQuery = ref('')
const roleFilter = ref('')
const showEditModal = ref(false)
const showDeleteModal = ref(false)
const selectedUser = ref(null)

const editForm = ref({
  name: '',
  email: '',
  role: '',
  branch_id: '',
  status: ''
})

// API instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const showNotification = (message, type = 'success') => {
  toastMessage.value = message
  toastType.value = type
  showToast.value = true
  setTimeout(() => {
    showToast.value = false
  }, 3000)
}

const fetchUsers = async () => {
  try {
    const userData = sessionStorage.getItem('user')
    if (userData) {
      const user = JSON.parse(userData)
      const response = await api.get(`/api/users/subsidiary/${user.subsidiary_id}`)
      users.value = response.data.map(user => ({
        ...user,
        status: user.status || 'Active'
      }))
    }
  } catch (error) {
    showNotification('Failed to load users', 'error')
  }
}

const fetchBranches = async () => {
  try {
    const userData = sessionStorage.getItem('user')
    if (userData) {
      const user = JSON.parse(userData)
      const response = await api.get(`/api/branches?subsidiary_id=${user.subsidiary_id}`)
      branches.value = response.data
    }
  } catch (error) {
    showNotification('Failed to load branches', 'error')
  }
}

const filteredUsers = computed(() => {
  return users.value.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchQuery.value.toLowerCase())
    const matchesRole = !roleFilter.value || user.role === roleFilter.value
    return matchesSearch && matchesRole
  }).map(user => ({
    ...user,
    status: 'Active' // All users are considered active by default
  }))
})

const handleSearch = () => {
  // Search is handled by the computed property
}

const handleFilter = () => {
  // Filtering is handled by the computed property
}

const editUser = (user) => {
  selectedUser.value = user
  editForm.value = {
    name: user.name,
    email: user.email,
    role: user.role,
    branch_id: user.branch_id || '',
    status: user.status
  }
  showEditModal.value = true
}

const closeEditModal = () => {
  showEditModal.value = false
  selectedUser.value = null
  editForm.value = {
    name: '',
    email: '',
    role: '',
    branch_id: '',
    status: ''
  }
}

const handleEditSubmit = async () => {
  try {
    isLoading.value = true
    // Remove status from the request since it's not in the database
    const { status, ...userData } = editForm.value
    
    // Convert empty branch_id to null
    if (userData.branch_id === '') {
      userData.branch_id = null
    }
    
    const response = await api.put(`/api/users/${selectedUser.value.id}`, userData)
    if (response.data.success) {
      showNotification('User updated successfully')
      await fetchUsers()
      closeEditModal()
    } else {
      showNotification(response.data.message || 'Failed to update user', 'error')
    }
  } catch (error) {
    showNotification('Failed to update user', 'error')
  } finally {
    isLoading.value = false
  }
}

const confirmDelete = (user) => {
  selectedUser.value = user
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  selectedUser.value = null
}

const handleDelete = async () => {
  try {
    isLoading.value = true
    const response = await api.delete(`/api/users/${selectedUser.value.id}`)
    if (response.data.success) {
      showNotification('User deleted successfully')
      await fetchUsers()
      closeDeleteModal()
    } else {
      showNotification(response.data.message || 'Failed to delete user', 'error')
    }
  } catch (error) {
    if (error.response?.status === 404) {
      showNotification('User not found or already deleted', 'error')
    } else {
      showNotification('Failed to delete user', 'error')
    }
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  // Check if user is admin
  const userData = sessionStorage.getItem('user')
  if (userData) {
    const user = JSON.parse(userData)
    if (user.role !== 'Admin' && user.role !== 'admin') {
      router.push('/')
    } else {
      await Promise.all([fetchUsers(), fetchBranches()])
    }
  } else {
    router.push('/')
  }
})
</script>

<style scoped>
.user-management-page {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.header-content h1 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.025em;
}

.subtitle {
  font-size: 0.875rem;
  color: #64748b;
}

.management-container {
  width: 100%;
}

.filters-section {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}

.search-box {
  position: relative;
  flex: 1;
}

.search-box input {
  width: 100%;
  padding: 12px 16px;
  padding-left: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
}

.search-box i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
}

.filter-group select {
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  min-width: 150px;
  background-color: white;
  color: #1e293b;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  transition: all 0.2s ease;
}

.filter-group select:hover {
  border-color: #94a3b8;
}

.filter-group select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  overflow: hidden;
}

.table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

th {
  background: #f8fafc;
  font-weight: 600;
  color: #64748b;
  font-size: 0.875rem;
}

td {
  color: #1e293b;
  font-size: 0.875rem;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-badge.active {
  background: #dcfce7;
  color: #16a34a;
}

.status-badge.inactive {
  background: #fee2e2;
  color: #dc2626;
}

.actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  padding: 6px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn.edit {
  color: #2563eb;
}

.action-btn.delete {
  color: #dc2626;
}

.action-btn:hover {
  opacity: 0.8;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f8fafc;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.modal-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.close-btn {
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}

.btn-primary,
.btn-secondary,
.btn-danger {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn-danger {
  background: #dc2626;
  color: white;
}

.btn-danger:hover {
  background: #b91c1c;
}

.toast-container {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 1000;
}

.toast {
  padding: 16px;
  border-radius: 12px;
  font-weight: 500;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: slideIn 0.3s ease-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
}

.toast.success {
  background: #dcfce7;
  color: #16a34a;
}

.toast.error {
  background: #fee2e2;
  color: #dc2626;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .user-management-page {
    padding: 16px;
  }

  .filters-section {
    flex-direction: column;
  }

  .filter-group select {
    width: 100%;
  }

  .table-container {
    margin: 0 -16px;
  }

  table {
    min-width: 800px;
  }

  .modal-content {
    margin: 16px;
    max-height: calc(100vh - 32px);
  }
}

/* Common select styles for both main page and modal */
select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  background-color: white;
  color: #1e293b;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  transition: all 0.2s ease;
}

select:hover {
  border-color: #94a3b8;
}

select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

select option {
  padding: 8px;
  font-size: 0.875rem;
  color: #1e293b;
  background-color: white;
}

select option:hover {
  background-color: #f1f5f9;
}
</style> 